import {
  useIsFetching,
  useQueryClient,
  useQuery,
  type Query,
  type UseQueryResult,
} from '@tanstack/react-query';
import { QUERY_KEYS } from './query-keys';

/** The criteria of the search currently on screen. Both fields are required (US-005 BR-02). */
export interface PolicySearchCriteria {
  readonly policyNumber: string;
  readonly dateOfBirth: string;
}

const ACTIVE_SEARCH_KEY = QUERY_KEYS.activeSearch;

/**
 * The active search lives in the TanStack Query cache rather than in `store/`.
 *
 * Two reasons: a policy number and a date of birth are PHI and `store/` must never hold them, and the
 * query cache already survives navigating to Contact and back (US-006 AC-003) while being wiped on
 * sign-out along with every response it keyed. Nothing is written to disk either way.
 */
export function useActiveSearch(): UseQueryResult<PolicySearchCriteria | null> {
  return useQuery<PolicySearchCriteria | null>({
    queryKey: ACTIVE_SEARCH_KEY,
    // Client-owned state: never fetched, only set by the search form.
    queryFn: () => null,
    initialData: null,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/**
 * Whether the search currently on screen is still resolving.
 *
 * It counts the policy lookup and the claims lookup together: one submit fires both, and a provider
 * who can re-submit while either is in flight gets a second pair of requests over a half-drawn page.
 *
 * This lives in `lib/` because it is the only place that can see both — `features/policy` may not
 * import `features/claims`. It matches on the queries' key prefixes rather than on the criteria, so
 * it needs nothing from either feature. The active search itself is deliberately not counted: it
 * never fetches, it only holds what the form last submitted.
 */
export function useSearchInFlight(): boolean {
  const policy = useIsFetching({ queryKey: QUERY_KEYS.policyDetails, predicate: hasNothingToShow });
  const claims = useIsFetching({ queryKey: QUERY_KEYS.claims, predicate: hasNothingToShow });

  return policy + claims > 0;
}

/**
 * Only a query with no data yet blocks the form.
 *
 * Both queries carry a five-minute `staleTime`, so returning to the workspace re-fetches the search
 * already on screen in the background. Counting that would disable the Search button and label it
 * "Searching…" while the provider is reading results and typing the next lookup — the opposite of
 * what the block is for.
 */
function hasNothingToShow(query: Query): boolean {
  return query.state.data === undefined;
}

/** Replaces the active search, which re-runs every query keyed on it (US-013 AC-002). */
export function useSetActiveSearch(): (criteria: PolicySearchCriteria) => void {
  const queryClient = useQueryClient();

  return (criteria) => {
    queryClient.setQueryData(ACTIVE_SEARCH_KEY, criteria);
  };
}
